import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserTable } from '@/components/UserTable';
import { PendingInvitationsTable } from '@/components/PendingInvitationsTable';
import { InviteUserDialog } from '@/components/InviteUserDialog';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { AppLayout } from '@/components/AppLayout';

type UserRole = 'owner' | 'admin' | 'recruiter' | 'viewer';

interface User {
  userId: string;
  email: string;
  name: string;
  role: UserRole;
  status: 'active' | 'disabled';
}

interface Invitation {
  invitationId: string;
  inviteeEmail: string;
  role: UserRole;
  inviterUserId: string;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
}

export function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const canInviteUsers =
    currentUser?.role === 'owner' || currentUser?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [usersData, invitationsData] = await Promise.all([
        apiClient.get<User[]>('/users'),
        canInviteUsers ? apiClient.get<Invitation[]>('/invitations') : Promise.resolve([]),
      ]);

      setUsers(usersData);
      setInvitations(invitationsData);
    } catch (err: any) {
      setError(err.message || 'Falha ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">Carregando...</div>
      </div>
    );
  }

  return (
    <AppLayout
      actions={
        <Button variant="outline" onClick={() => navigate('/dashboard')}>
          Dashboard
        </Button>
      }
    >
      <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Gerenciamento de Usuários</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie os membros da sua organização
          </p>
        </div>
        {canInviteUsers && (
          <Button onClick={() => setInviteDialogOpen(true)}>
            Convidar Usuário
          </Button>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-4 rounded">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Usuários Ativos ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum usuário encontrado
            </div>
          ) : (
            <UserTable users={users} onUserUpdated={fetchData} />
          )}
        </CardContent>
      </Card>

      {canInviteUsers && (
        <Card>
          <CardHeader>
            <CardTitle>Convites Pendentes ({invitations.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <PendingInvitationsTable
              invitations={invitations}
              users={users}
              onInvitationCancelled={fetchData}
            />
          </CardContent>
        </Card>
      )}

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onSuccess={fetchData}
      />
    </div>
    </AppLayout>
  );
}
